import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useCart } from "../../contexts/CartContext";
import ProductReview from "./ProductReview";
import RelatedProduct from "./RelatedProduct";
import { Footer } from "../common";
import "../../styles/productDetail.css";
import { addToCart } from "../../services/cart";
import { sendMessage } from "../../services/chat";
import { toast } from "react-toastify";
import { getAuthMode } from "../../utils/authHelpers";
import { trackProductView } from "../../services/recentlyViewed";
import {
  addToWishlist,
  removeFromWishlist,
  checkWishlistStatus,
} from "../../services/wishlist";
import { getProductBySlug, getProductById } from "../../services/products";

// ── Helpers ────────────────────────────────────────────
const placeholderImg = "";
const normUrl = (u) =>
  typeof u === "string" ? u.replace("http://", "https://") : u;
const onImgError = (e) => {
  e.currentTarget.onerror = null;
  e.currentTarget.src = placeholderImg;
};

const parseList = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) {
    if (value.length === 0) return [];
    if (
      value.length === 1 &&
      typeof value[0] === "string" &&
      value[0].includes(",")
    )
      return value[0]
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
    return value.map((v) => String(v).trim()).filter(Boolean);
  }
  if (typeof value === "string")
    return value
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
  return [];
};

const resolvePrice = (p) => {
  const final    = Number(p?.final_price              ?? 0);
  const flash    = Number(p?.flash_sale_price         ?? 0);
  const discount = Number(p?.product_discount_price   ?? 0);
  const regular  = Number(p?.price                    ?? 0);
  if (final    > 0) return final;
  if (flash    > 0) return flash;
  if (discount > 0) return discount;
  return regular;
};

const mapProduct = (p) => {
  const originalPrice = Number(p?.price ?? 0);
  const displayPrice  = resolvePrice(p);
  const isFlashSale   = !!p?.is_flash_sale;
  const pricingType   = p?.pricing_type ?? "normal";
  const discount      = p?.discount ? Number(p.discount) : 0;
  const discountType  = p?.discount_type ?? null;

  return {
    id          : p?.id,
    slug        : p?.slug,
    title       : p?.name ?? "",
    price       : displayPrice,
    originalPrice,
    isFlashSale,
    pricingType,
    discount,
    discountType,
    weight      : p?.weight      ?? null,
    weight_unit : p?.weight_unit ?? "kg",
    rating      : Number(p?.average_rating ?? 0),
    reviews     : Number(p?.total_reviews  ?? 0),
    sold        : Number(p?.sold_count ?? p?.units_sold ?? 0),
    brand       : p?.brand       ?? "",
    description : p?.description ?? "",
    colors      : parseList(p?.color),
    sizes       : parseList(p?.size),
    images:
      Array.isArray(p?.product_images) && p.product_images.length
        ? p.product_images.map(normUrl)
        : p?.image_url
          ? [normUrl(p.image_url)]
          : [],
    features  : p?.tags ? parseList(p.tags) : [],
    categoryId: p?.category?.id  ?? null,
    vendor    : p?.vendor        ?? null,
    details   : {
      productName     : p?.name             ?? "",
      material        : p?.material         ?? "",
      color           : parseList(p?.color) ?? "",
      packSize        : p?.pack_size        ?? "",
      packageIncluded : p?.package_included ?? "",
      notes           : p?.notes            ?? [],
    },
  };
};

// ── Icons ──────────────────────────────────────────────
const HomeIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    <polyline points="9 22 9 12 15 12 15 22" />
  </svg>
);
const ChevronRight = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);
const HeartIcon = ({ filled }) => (
  <svg width="18" height="18" viewBox="0 0 24 24">
    <path
      d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"
      fill={filled ? "currentColor" : "none"}
      stroke="currentColor"
      strokeWidth="2"
    />
  </svg>
);
const CartIcon = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" />
    <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
  </svg>
);
const BoltIcon = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
  </svg>
);
const CloseIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);
const SendIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="22" y1="2" x2="11" y2="13" />
    <polygon points="22 2 15 22 11 13 2 9 22 2" />
  </svg>
);
const FlashIcon = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor" stroke="none">
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
  </svg>
);
const LockIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

// ── Star renderer ──────────────────────────────────────
const renderStars = (rating) => {
  const full    = Math.floor(rating || 0);
  const hasHalf = (rating || 0) % 1 >= 0.5;
  const stars   = [];
  for (let i = 0; i < full; i++)
    stars.push(
      <svg key={`f${i}`} className="pd-star filled" width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" />
      </svg>,
    );
  if (hasHalf && full < 5)
    stars.push(
      <svg key="half" className="pd-star half" width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
        <defs>
          <linearGradient id="halfPD">
            <stop offset="50%" stopColor="#FFD700" />
            <stop offset="50%" stopColor="#E5E5E5" />
          </linearGradient>
        </defs>
        <path fill="url(#halfPD)" d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" />
      </svg>,
    );
  const empty = 5 - Math.ceil(rating || 0);
  for (let i = 0; i < empty; i++)
    stars.push(
      <svg key={`e${i}`} className="pd-star empty" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" />
      </svg>,
    );
  return stars;
};

// ── Auth Required Modal ────────────────────────────────
const AuthRequiredModal = ({ onClose, onLogin, onGuest, actionLabel }) => (
  <>
    <style>{`
      .auth-modal-overlay {
        position: fixed; inset: 0;
        background: rgba(14,56,100,0.45);
        backdrop-filter: blur(4px); -webkit-backdrop-filter: blur(4px);
        z-index: 1100;
        display: flex; align-items: center; justify-content: center;
        padding: 16px;
        animation: authOverlayIn 0.2s ease;
      }
      @keyframes authOverlayIn { from{opacity:0} to{opacity:1} }
      .auth-modal {
        background: #fff; border-radius: 20px; width: 100%; max-width: 400px;
        padding: 0; overflow: hidden;
        box-shadow: 0 24px 64px rgba(14,56,100,0.18), 0 4px 16px rgba(0,0,0,0.08);
        animation: authModalIn 0.25s cubic-bezier(0.34,1.56,0.64,1);
        position: relative;
      }
      @keyframes authModalIn {
        from{opacity:0;transform:scale(0.88) translateY(16px)}
        to  {opacity:1;transform:scale(1) translateY(0)}
      }
      .auth-modal-header-band {
        background: linear-gradient(135deg,#f77321 0%,#e0631a 100%);
        padding: 28px 24px 24px; text-align: center; position: relative;
      }
      .auth-modal-close-btn {
        position: absolute; top: 12px; right: 12px;
        background: rgba(255,255,255,0.2); border: none; border-radius: 50%;
        width: 30px; height: 30px; display: flex; align-items: center; justify-content: center;
        cursor: pointer; color: #fff; transition: background 0.15s;
      }
      .auth-modal-close-btn:hover { background: rgba(255,255,255,0.35); }
      .auth-modal-icon-wrap {
        width: 64px; height: 64px; background: rgba(255,255,255,0.2); border-radius: 50%;
        display: flex; align-items: center; justify-content: center;
        margin: 0 auto 14px; color: #fff;
      }
      .auth-modal-heading { font-size: 20px; font-weight: 700; color: #fff; margin: 0 0 4px; line-height: 1.25; }
      .auth-modal-subtext { font-size: 13.5px; color: rgba(255,255,255,0.88); margin: 0; line-height: 1.45; }
      .auth-modal-body { padding: 24px; display: flex; flex-direction: column; gap: 12px; }
      .auth-modal-action-label {
        font-size: 12.5px; color: #6b7280; text-align: center; margin: 0 0 4px;
        font-weight: 500; letter-spacing: 0.02em; text-transform: uppercase;
      }
      .auth-btn-login {
        width: 100%; padding: 13px 20px;
        background: linear-gradient(135deg,#f77321 0%,#e0631a 100%);
        color: #fff; border: none; border-radius: 12px;
        font-size: 15px; font-weight: 700; cursor: pointer;
        display: flex; align-items: center; justify-content: center; gap: 8px;
        transition: transform 0.15s, box-shadow 0.15s, filter 0.15s;
        box-shadow: 0 4px 14px rgba(247,115,33,0.35); letter-spacing: 0.01em;
      }
      .auth-btn-login:hover { filter: brightness(1.06); transform: translateY(-1px); box-shadow: 0 6px 20px rgba(247,115,33,0.42); }
      .auth-btn-login:active { transform: translateY(0); }
      .auth-modal-footer-note { font-size: 12px; color: #9ca3af; text-align: center; margin: 0; padding: 0 4px; line-height: 1.5; }
    `}</style>
    <div className="auth-modal-overlay" onClick={onClose}>
      <div className="auth-modal" onClick={(e) => e.stopPropagation()}>
        <div className="auth-modal-header-band">
          <button className="auth-modal-close-btn" onClick={onClose} aria-label="Close">
            <CloseIcon />
          </button>
          <div className="auth-modal-icon-wrap"><LockIcon /></div>
          <h2 className="auth-modal-heading">Login Required</h2>
          <p className="auth-modal-subtext">
            Sign in to {actionLabel} and enjoy a seamless shopping experience.
          </p>
        </div>
        <div className="auth-modal-body">
          <p className="auth-modal-action-label">Choose how to continue</p>
          <button className="auth-btn-login" onClick={onLogin}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
              <polyline points="10 17 15 12 10 7" />
              <line x1="15" y1="12" x2="3" y2="12" />
            </svg>
            Login to your account
          </button>
          <p className="auth-modal-footer-note">
            Guest checkout is limited. Login for order history, wishlist &amp; faster checkout.
          </p>
        </div>
      </div>
    </div>
  </>
);

// ── Component ──────────────────────────────────────────
const ProductDetail = () => {
  const { slug }   = useParams();
  const navigate   = useNavigate();
  const { user }   = useAuth();
  const { updateCartCount } = useCart();

  const [product,           setProduct          ] = useState(null);
  const [selectedColor,     setSelectedColor    ] = useState("Default");
  const [selectedSize,      setSelectedSize     ] = useState("One Size");
  const [quantity,          setQuantity         ] = useState(1);
  const [loading,           setLoading          ] = useState(true);
  const [selectedImageIndex,setSelectedImageIndex]=useState(0);
  const [showMessageModal,  setShowMessageModal ] = useState(false);
  const [messageText,       setMessageText      ] = useState("");
  const [sendingMessage,    setSendingMessage   ] = useState(false);
  const [wishlistItem,      setWishlistItem     ] = useState(null);
  const [wishlistLoading,   setWishlistLoading  ] = useState(false);

  // pendingAction: null | "cart" | "buyNow"
  const [authModalAction, setAuthModalAction] = useState(null);

  // Live rating lifted from ProductReview
  const [liveRating,  setLiveRating ] = useState(null);
  const [liveReviews, setLiveReviews] = useState(null);

  const handleStatsLoad = ({ average, total }) => {
    setLiveRating(average);
    setLiveReviews(total);
  };

  const displayRating  = liveRating  !== null ? liveRating  : (product?.rating  ?? 0);
  const displayReviews = liveReviews !== null ? liveReviews : (product?.reviews  ?? 0);

  const [showZoom,  setShowZoom ] = useState(false);
  const [zoomStyle, setZoomStyle] = useState({});

  const handleZoomMove = (e) => {
    const imageUrl = product?.images?.[selectedImageIndex] || placeholderImg;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width)  * 100;
    const y = ((e.clientY - rect.top)  / rect.height) * 100;
    setZoomStyle({ backgroundImage: `url(${imageUrl})`, backgroundPosition: `${x}% ${y}%` });
  };

  // ── Load product ───────────────────────────────────
  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      setLiveRating(null);
      setLiveReviews(null);
      try {
        let pRaw = null;
        try {
          const res = await getProductBySlug(slug);
          pRaw = res?.data?.data || res?.data || null;
        } catch (e1) {
          const status    = e1?.response?.status;
          const isNumeric = /^\d+$/.test(String(slug));
          if (status === 404 && isNumeric) {
            const res2 = await getProductById(slug);
            pRaw = res2?.data?.data || res2?.data || null;
          } else throw e1;
        }
        if (!pRaw) { if (mounted) setProduct(null); return; }
        const mapped = mapProduct(pRaw);
        if (mapped?.slug && mapped.slug !== String(slug)) {
          navigate(`/product/${mapped.slug}`, { replace: true });
          return;
        }
        if (mounted) {
          setProduct(mapped);
          setSelectedColor(mapped.colors?.[0] || "Default");
          setSelectedSize(mapped.sizes?.[0]   || "One Size");
          setSelectedImageIndex(0);
        }
      } catch (err) {
        console.error("[DETAIL] fetch failed:", err);
        if (mounted) setProduct(null);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => { mounted = false; };
  }, [slug, navigate]);

  // ── Wishlist status ──────────────────────────────
  useEffect(() => {
    let mounted = true;
    const load = async () => {
      if (!product?.id) return;
      try {
        const item = await checkWishlistStatus(product.id);
        if (mounted) setWishlistItem(item || null);
      } catch {}
    };
    load();
    return () => { mounted = false; };
  }, [product?.id]);

  // ── Track view ───────────────────────────────────
  useEffect(() => {
    if (!product?.id) return;
    trackProductView(product.id).catch(() => {});
  }, [product?.id]);

  // ── Auth check ───────────────────────────────────
  // Allows both logged-in users AND guests with a token
  const isAuthenticated = () => {
    const mode = getAuthMode();
    return mode === "user" || mode === "guest";
  };

  const handleAuthLogin = () => {
    setAuthModalAction(null);
    navigate("/login");
  };

  const handleAuthGuest = () => {
    setAuthModalAction(null);
    navigate("/guest-checkout");
  };

  // ── Wishlist ─────────────────────────────────────
  const handleToggleWishlist = async () => {
    if (!product?.id) return;
    if (!user) { toast.warning("Please login to use wishlist"); return; }
    if (wishlistLoading) return;
    setWishlistLoading(true);
    try {
      if (wishlistItem) {
        await removeFromWishlist(wishlistItem.id);
        setWishlistItem(null);
        toast.success("Removed from wishlist");
      } else {
        const res = await addToWishlist(product.id);
        const ni  = res?.data?.data || res?.data || res;
        setWishlistItem(ni || { product_id: product.id });
        toast.success("Added to wishlist");
      }
    } catch {
      toast.error("Wishlist update failed. Please try again.");
    } finally {
      setWishlistLoading(false);
    }
  };

  // ── Add to Cart ───────────────────────────────────
  const handleAddToCart = async () => {
    if (!product) return;
    if (!isAuthenticated()) {
      setAuthModalAction("cart");
      return;
    }
    try {
      await addToCart({
        productId: product.id,
        quantity,
        color: selectedColor || null,
        size : selectedSize  || null,
      });
      toast.success("Added to cart!");
      updateCartCount();
    } catch {
      toast.error("Failed to add to cart. Please try again.");
    }
  };

  // ── Buy Now ───────────────────────────────────────
  const handleBuyNow = () => {
    if (!product) return;
    if (!isAuthenticated()) {
      setAuthModalAction("buyNow");
      return;
    }
    navigate("/checkout", {
      state: {
        items: [
          {
            productId : product.id,
            name      : product.title,
            brand     : product.brand || "",
            weight    : product.weight    ?? 0,
            weight_unit: product.weight_unit ?? "kg",
            storeId   : product.vendor?.id || "default",
            storeName : product.vendor?.store_name || "Store",
            color     : selectedColor,
            size      : selectedSize,
            price     : Number(product.price),
            quantity,
            image     : product.images?.[0] || "",
            selected  : true,
          },
        ],
        mode     : getAuthMode(),  // "user" | "guest"
        isBuyNow : true,
      },
    });
  };

  // ── Message ───────────────────────────────────────
  const handleSendMessage = async () => {
    if (!messageText.trim()) { toast.warning("Please enter a message"); return; }
    if (!user) { toast.warning("Please login to send messages"); return; }
    try {
      setSendingMessage(true);
      await sendMessage({ productId: product.id, message: messageText.trim() });
      toast.success("Message sent!");
      setMessageText("");
      setShowMessageModal(false);
    } catch {
      toast.error("Failed to send message.");
    } finally {
      setSendingMessage(false);
    }
  };

  // ── Loading ───────────────────────────────────────
  if (loading)
    return (
      <div className="pd-page">
        <div className="pd-loading">
          <div className="pd-spinner" />
          <span>Loading product details…</span>
        </div>
      </div>
    );

  if (!product)
    return (
      <div className="pd-page">
        <div className="pd-not-found">
          <div className="pd-not-found-icon">🔍</div>
          <h2 className="pd-not-found-title">Product not found</h2>
          <p className="pd-not-found-sub">This product may have been removed or the link is incorrect.</p>
          <button className="pd-btn pd-btn-primary" onClick={() => navigate(-1)}>← Go Back</button>
        </div>
      </div>
    );

  const hasDiscount = product.originalPrice > product.price && product.originalPrice > 0;
  const discountPct = hasDiscount
    ? Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100)
    : 0;

  const authActionLabel =
    authModalAction === "cart" ? "add items to cart" : "proceed to checkout";

  return (
    <div className="pd-page">
      {/* Auth modal */}
      {authModalAction && (
        <AuthRequiredModal
          actionLabel={authActionLabel}
          onClose={() => setAuthModalAction(null)}
          onLogin={handleAuthLogin}
          onGuest={handleAuthGuest}
        />
      )}

      <div className="pd-container">
        {/* Breadcrumb */}
        <nav className="pd-breadcrumb">
          <button className="pd-bc-link" onClick={() => navigate("/")}>
            <HomeIcon /> Home
          </button>
          <span className="pd-bc-sep"><ChevronRight /></span>
          <button className="pd-bc-link" onClick={() => navigate("/explore")}>
            {product.categoryId ? "Category" : "Collections"}
          </button>
          <span className="pd-bc-sep"><ChevronRight /></span>
          <span className="pd-bc-current">{product.title}</span>
        </nav>

        {/* Main card */}
        <div className="pd-card">
          {/* Gallery */}
          <div className="pd-gallery">
            <div
              className="pd-main-img-wrap"
              onMouseEnter={() => setShowZoom(true)}
              onMouseLeave={() => setShowZoom(false)}
              onMouseMove={handleZoomMove}
            >
              {product.isFlashSale && (
                <span className="pd-discount-badge pd-badge-flash">
                  <FlashIcon /> Flash Sale
                </span>
              )}
              {!product.isFlashSale && hasDiscount && discountPct > 0 && (
                <span className="pd-discount-badge">
                  {product.discountType === "amount"
                    ? `-$${Number(product.discount).toFixed(0)}`
                    : `-${discountPct}%`}
                </span>
              )}
              <img
                src={product.images?.[selectedImageIndex] || placeholderImg}
                alt={product.title}
                className="pd-main-img"
                onError={onImgError}
              />
              {showZoom && <div className="pd-image-zoom" style={zoomStyle} />}
            </div>

            {product.images?.length > 1 && (
              <div className="pd-thumbs">
                {product.images.map((img, i) => (
                  <button
                    key={i}
                    className={`pd-thumb ${selectedImageIndex === i ? "active" : ""}`}
                    onClick={() => setSelectedImageIndex(i)}
                  >
                    <img src={img} alt={`${product.title} ${i + 1}`} onError={onImgError} />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info */}
          <div className="pd-info">
            {/* Title + wishlist */}
            <div className="pd-title-row">
              <h1 className="pd-title">{product.title}</h1>
              <button
                className={`pd-wishlist-btn ${wishlistItem ? "active" : ""}`}
                onClick={handleToggleWishlist}
                disabled={wishlistLoading}
                title={wishlistItem ? "Remove from wishlist" : "Add to wishlist"}
              >
                <HeartIcon filled={!!wishlistItem} />
              </button>
            </div>

            {/* Rating */}
            <div className="pd-rating-row">
              <div className="pd-stars">{renderStars(displayRating)}</div>
              <span className="pd-rating-val">{Number(displayRating).toFixed(1)}</span>
              <span className="pd-rating-sep">·</span>
              <span className="pd-reviews">{displayReviews} reviews</span>
              {product.sold > 0 && (
                <>
                  <span className="pd-rating-sep">·</span>
                  <span className="pd-sold">{product.sold} sold</span>
                </>
              )}
            </div>

            {/* Brand */}
            {product.brand && (
              <div className="pd-brand-row">
                <span className="pd-brand-label">Brand</span>
                <span className="pd-brand-value">{product.brand}</span>
              </div>
            )}

            {/* Price */}
            <div className="pd-price-block">
              {product.isFlashSale && (
                <span className="pd-flash-label"><FlashIcon /> Flash Sale Price</span>
              )}
              <div className="pd-price-row">
                <span className={`pd-price ${product.isFlashSale ? "pd-price-flash" : ""}`}>
                  ${Number(product.price).toFixed(2)}
                </span>
                {hasDiscount && (
                  <>
                    <span className="pd-original-price">${Number(product.originalPrice).toFixed(2)}</span>
                    {discountPct > 0 && (
                      <span className="pd-discount-pill">
                        {product.isFlashSale
                          ? `-${discountPct}% Flash`
                          : product.discountType === "amount"
                            ? `-$${Number(product.discount).toFixed(2)}`
                            : `-${discountPct}%`}
                      </span>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Description */}
            {product.description && (
              <p className="pd-description">{product.description}</p>
            )}

            <div className="pd-divider" />

            {/* Colors */}
            {product.colors?.length > 0 && (
              <div className="pd-option-group">
                <span className="pd-option-label">Color: <strong>{selectedColor}</strong></span>
                <div className="pd-option-chips">
                  {product.colors.map((c) => (
                    <button key={c} className={`pd-chip ${selectedColor === c ? "active" : ""}`} onClick={() => setSelectedColor(c)}>
                      {c}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Sizes */}
            {product.sizes?.length > 0 && (
              <div className="pd-option-group">
                <span className="pd-option-label">Size: <strong>{selectedSize}</strong></span>
                <div className="pd-option-chips">
                  {product.sizes.map((s) => (
                    <button key={s} className={`pd-chip ${selectedSize === s ? "active" : ""}`} onClick={() => setSelectedSize(s)}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Quantity */}
            <div className="pd-option-group pd-qty-group">
              <span className="pd-option-label">Quantity</span>
              <div className="pd-qty-controls">
                <button className="pd-qty-btn" onClick={() => setQuantity((q) => Math.max(1, q - 1))} disabled={quantity <= 1}>−</button>
                <span className="pd-qty-val">{quantity}</span>
                <button className="pd-qty-btn pd-qty-inc" onClick={() => setQuantity((q) => q + 1)}>+</button>
              </div>
            </div>

            <div className="pd-divider" />

            {/* Actions */}
            <div className="pd-actions">
              <button className="pd-btn pd-btn-primary"   onClick={handleBuyNow}>
                <BoltIcon /> Buy Now
              </button>
              <button className="pd-btn pd-btn-secondary" onClick={handleAddToCart}>
                <CartIcon /> Add to Cart
              </button>
            </div>

            {/* Vendor */}
            {product.vendor?.store_name && (
              <div className="pd-vendor-strip">
                <div className="pd-vendor-icon">🏪</div>
                <div className="pd-vendor-info">
                  <span className="pd-vendor-label">Sold by</span>
                  <span className="pd-vendor-name">{product.vendor.unique_name}</span>
                </div>
                <div className="pd-vendor-actions">
                  {product.vendor?.unique_name && (
                    <button className="pd-vendor-visit-btn" onClick={() => navigate(`/store/${product.vendor.unique_name}`)}>
                      Visit Store →
                    </button>
                  )}
                  <button className="pd-vendor-msg-btn" onClick={() => setShowMessageModal(true)}>
                    Message
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Reviews */}
        <ProductReview
          productId={product.id}
          productTitle={product.title}
          fallbackRating={product.rating}
          fallbackReviewCount={product.reviews}
          onStatsLoad={handleStatsLoad}
        />

        {/* Related */}
        <RelatedProduct
          categoryId={product.categoryId}
          excludeId={product.id}
          onNavigate={(payload) => {
            const nextSlug = typeof payload === "object" ? payload.slug : String(payload);
            if (nextSlug) navigate(`/product/${nextSlug}`);
            else toast.error("Missing product slug");
          }}
        />
      </div>

      {/* Message modal */}
      {showMessageModal && (
        <div className="pd-modal-overlay" onClick={() => setShowMessageModal(false)}>
          <div className="pd-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pd-modal-header">
              <h3 className="pd-modal-title">Message {product.vendor?.store_name || "Vendor"}</h3>
              <button className="pd-modal-close" onClick={() => setShowMessageModal(false)}><CloseIcon /></button>
            </div>
            <div className="pd-modal-product">
              <img src={product.images?.[0] || placeholderImg} alt={product.title} onError={onImgError} />
              <div>
                <div className="pd-modal-product-name">{product.title}</div>
                <div className="pd-modal-product-price">${Number(product.price).toFixed(2)}</div>
              </div>
            </div>
            <div className="pd-modal-body">
              <label className="pd-modal-label">Your message</label>
              <textarea
                className="pd-modal-textarea"
                placeholder="Ask about this product, shipping, availability…"
                rows={4}
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
                disabled={sendingMessage}
              />
            </div>
            <div className="pd-modal-footer">
              <button className="pd-btn pd-btn-ghost" onClick={() => setShowMessageModal(false)}>Cancel</button>
              <button className="pd-btn pd-btn-primary" onClick={handleSendMessage} disabled={sendingMessage || !messageText.trim()}>
                {sendingMessage ? <><span className="pd-btn-spinner" /> Sending…</> : <><SendIcon /> Send</>}
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default ProductDetail;